/* ============================================================
   Portl sale popup + floating badge controller.
   Namespace: window.PortlSalePopup

   Lifecycle per "Popup version":
     fresh              -> popup after delay
     main close / overlay click -> minimize to badge (sale_popup_minimized_<v>)
     badge click        -> reopen popup
     banner click       -> permanent dismiss (sale_popup_dismissed_<v>) + redirect
     badge close        -> permanent dismiss (removes popup + badge)

   All listeners are bound through one AbortController so destroy() removes
   them cleanly (no duplicates / leaks) on theme-editor re-init. Fails safe:
   any error just leaves the site untouched.
   ============================================================ */
window.PortlSalePopup = (function () {
  'use strict';

  var current = null;

  function create(root) {
    var ac = new AbortController();
    var signal = ac.signal;
    var timers = [];
    function on(el, ev, fn, opts) {
      if (!el) return;
      var o = { signal: signal };
      if (opts) { for (var k in opts) o[k] = opts[k]; }
      el.addEventListener(ev, fn, o);
    }
    function later(fn, ms) { var t = window.setTimeout(fn, ms); timers.push(t); return t; }

    var designMode = !!(window.Shopify && window.Shopify.designMode);
    var version = root.getAttribute('data-psp-version') || '1';
    var delay = parseInt(root.getAttribute('data-psp-delay'), 10);
    if (isNaN(delay) || delay < 0) delay = 0;
    var badgeEnabled = root.getAttribute('data-psp-badge') === '1';
    var overlayCloses = root.getAttribute('data-psp-overlay-close') === '1';
    var target = root.getAttribute('data-psp-target') || '_self';

    var overlay = root.querySelector('[data-psp]');
    var box = root.querySelector('[data-psp-box]');
    var img = root.querySelector('[data-psp-img]');
    var closeBtn = root.querySelector('[data-psp-close]');
    var link = root.querySelector('[data-psp-link]');
    var badge = root.querySelector('[data-psp-badge]');
    var badgeOpen = root.querySelector('[data-psp-badge-open]');
    var badgeClose = root.querySelector('[data-psp-badge-close]');
    if (!overlay) return null;

    var DKEY = 'sale_popup_dismissed_' + version;
    var MKEY = 'sale_popup_minimized_' + version;
    var lastFocus = null;

    function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
    function lsSet(k, v) { if (designMode) return; try { window.localStorage.setItem(k, v); } catch (e) {} }

    var isDismissed = !designMode && lsGet(DKEY) === '1';
    var isMinimized = !designMode && lsGet(MKEY) === '1';

    /* ---------- focus trap ---------- */
    function focusables() {
      return Array.prototype.filter.call(
        overlay.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        function (el) { return el.offsetParent !== null; }
      );
    }
    function onKeydown(e) {
      if (overlay.hidden) return;
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); minimizeAction(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ---------- badge ---------- */
    function showBadge() {
      if (!badge) return;
      badge.hidden = false;
      window.requestAnimationFrame(function () { badge.classList.add('is-visible'); });
    }
    function hideBadge() {
      if (!badge) return;
      badge.classList.remove('is-visible');
      later(function () { badge.hidden = true; }, 260);
    }

    /* ---------- popup ---------- */
    function openPopup() {
      lastFocus = document.activeElement;
      if (badge) { badge.classList.remove('is-visible'); badge.hidden = true; }
      overlay.hidden = false;
      overlay.classList.remove('is-minimizing');
      window.requestAnimationFrame(function () { overlay.classList.add('is-open'); });
      document.body.classList.add('psp-scroll-lock');
      later(function () { try { (closeBtn || link || box).focus(); } catch (e) {} }, 60);
    }

    // Main close / overlay click → minimize into the badge (or dismiss if no badge).
    function minimizeAction() {
      if (!badgeEnabled || !badge) { dismiss(); return; }
      lsSet(MKEY, '1');
      overlay.classList.add('is-minimizing');
      overlay.classList.remove('is-open');
      document.body.classList.remove('psp-scroll-lock');
      later(function () { overlay.hidden = true; overlay.classList.remove('is-minimizing'); }, 280);
      showBadge();
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    }

    // Permanent dismiss → remove popup + badge for this version.
    function dismiss() {
      lsSet(DKEY, '1');
      overlay.classList.remove('is-open', 'is-minimizing');
      document.body.classList.remove('psp-scroll-lock');
      later(function () { overlay.hidden = true; }, 200);
      hideBadge();
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    }

    /* ---------- reveal (warm image, no flash) ---------- */
    function reveal() {
      if (img && img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
      if (!img || img.complete) { openPopup(); return; }
      var done = false;
      function go() { if (done) return; done = true; openPopup(); }
      on(img, 'load', go, { once: true });
      on(img, 'error', go, { once: true });
      later(go, 600);
    }

    /* ---------- wiring ---------- */
    on(document, 'keydown', onKeydown, { capture: true });
    on(closeBtn, 'click', function (e) { e.preventDefault(); minimizeAction(); });
    on(overlay, 'click', function (e) { if (e.target === overlay && overlayCloses) minimizeAction(); });
    // Banner click → permanent dismiss, then let the link navigate.
    on(link, 'click', function () {
      lsSet(DKEY, '1');
      if (target === '_blank') {
        overlay.classList.remove('is-open');
        document.body.classList.remove('psp-scroll-lock');
        later(function () { overlay.hidden = true; }, 0);
        hideBadge();
      }
    });
    on(badgeOpen, 'click', function (e) { e.preventDefault(); openPopup(); });
    on(badgeClose, 'click', function (e) { e.preventDefault(); e.stopPropagation(); dismiss(); });

    /* ---------- initial state ---------- */
    if (isDismissed) { /* show nothing */ }
    else if (isMinimized) { showBadge(); }
    else if (delay > 0) { later(reveal, delay); }
    else { reveal(); }

    return {
      destroy: function () {
        try { ac.abort(); } catch (e) {}
        timers.forEach(function (t) { clearTimeout(t); });
        document.body.classList.remove('psp-scroll-lock');
      }
    };
  }

  function boot() {
    try {
      if (current && current.destroy) { current.destroy(); }
      current = null;
      var root = document.querySelector('.portl-sale-popup-wrapper[data-psp-root]');
      if (root) current = create(root);
    } catch (e) {
      if (window.console && console.warn) console.warn('[PortlSalePopup] disabled:', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  // Theme editor: rebuild cleanly when the section is (re)loaded / selected.
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', boot);

  return { boot: boot, get controller() { return current; } };
})();
